import React, { useEffect, useState } from 'react';
import {
  Form, Button, Col, Row, Input, Select, DatePicker, Checkbox, Upload, message, Tag,
} from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { useApolloClient, useMutation } from '@apollo/client';
import moment from 'moment';
import { find } from 'lodash';
import {
  func, bool, shape, arrayOf,
} from 'prop-types';
import { REMOVE_UPLOAD, UPDATE_ACTIVITY } from '../../../graphql';
import { uploadFiles } from '../../../upload';
import Editor from '../../../components/editor';

const { Option } = Select;
const { Item: FormItem } = Form;

const EditActivity = ({
  cohort, setLoading, cohortLoading, closeModal, form, initValue,
}) => {
  const [instruction, setInstruction] = useState(initValue.instruction);
  const [updateActivity] = useMutation(UPDATE_ACTIVITY);
  const [removeUpload] = useMutation(REMOVE_UPLOAD);
  const client = useApolloClient();
  const [subjects, setSubjects] = useState([]);
  const [fileDeleteCache, setFileDeleteCache] = useState([]);

  const handleFormSubmit = async (inputs) => {
    const {
      attachment, dueDate, title, hasTextInput, embedLink,
    } = inputs;

    try {
      setLoading(true);

      // `subjectIds` is the whole set rather than a connect/disconnect pair,
      // so removing a subject is just leaving it out.
      await updateActivity({
        variables: {
          id: initValue.id,
          input: {
            title,
            instruction,
            embedLink,
            hasTextInput,
            dueDate: dueDate.toISOString(),
            subjectIds: inputs.subjects,
          },
        },
      });

      const added = (attachment?.fileList ?? [])
        .filter((file) => file.originFileObj)
        .map((file) => file.originFileObj);

      if (added.length > 0) {
        await uploadFiles({
          client, files: added, target: 'ACTIVITY', targetId: initValue.id,
        });
      }

      await Promise.all(fileDeleteCache.map(({ id }) => removeUpload({
        variables: { target: 'ACTIVITY', targetId: initValue.id, uploadId: id },
      })));

      form.resetFields();
      message.success('Activity updated successfully');
      return closeModal();
    } catch (err) {
      message.error(err.message || 'Error updating activity');
    } finally {
      setLoading(false);
    }
  };

  const getSubjects = (cohortId) => find(cohort, (c) => c.id === cohortId)?.subjects ?? [];
  // `uploads`, not `attachments`: the activity query selects uploads, and an
  // activity has no attachments field at all. Reading the wrong name here
  // threw on every open of this drawer, whatever the activity contained.
  // Submissions are the ones with attachments.
  const makeDefaultFileList = () => {
    const { uploads = [] } = initValue;
    const fileList = uploads.map(({
      id, url, filename,
    }) => ({
      uid: id,
      name: `${filename.slice(11) || 'File'}`,
      status: 'done',
      url,
    }));
    return fileList;
  };

  useEffect(() => {
    const initSubjectsOption = getSubjects(initValue.cohort.id);
    setSubjects(initSubjectsOption);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initValue]);

  return (
    <Form onFinish={handleFormSubmit} form={form} name="add-activity-form" layout="vertical" hideRequiredMark>
      <Row style={{ flexDirection: 'row' }}>
        <Col span={24} className="gx-mb-3">
          <Tag color="green" style={{ padding: '0.3rem 0.5rem' }}>
            <h5 className="gx-m-0">
              Created By:
              {' '}
              {initValue.user ? `${initValue.user.firstName} ${initValue.user.lastName}` : 'User Unavailable'}
            </h5>
          </Tag>
        </Col>
        <Col span={24}>
          <FormItem initialValue={initValue.title} name="title" label="Activity Title" rules={[{ required: true, message: 'Provide activity title' }]}>
            <Input placeholder="activity title" />
          </FormItem>
        </Col>
        <Col xm={24} md={12}>
          <FormItem initialValue={moment(initValue.dueDate)} name="dueDate" label="Due Date" rules={[{ required: true, message: 'Provide due date' }]}>
            <DatePicker className="gx-w-100" placeholder="select deu date" />
          </FormItem>
        </Col>
        <Col xm={24} md={12}>
          <FormItem initialValue={initValue.type} name="type" label="Activity Type">
            <Select>
              <Option key="ONLINE_CLASS">Online Class</Option>
              <Option key="ASSIGNMENT">Assignment</Option>
            </Select>
          </FormItem>
        </Col>
        <Col span={24}>
          <FormItem initialValue={initValue.embedLink} name="embedLink" label="Form Embed Link">
            <Input placeholder="e.g <iframe src='https://docs.google.com/forms../>" />
          </FormItem>
        </Col>
        <Col span={24}>
          <Editor overrideConfig={{ toolbarSticky: false }} content={instruction} setContent={setInstruction} />
        </Col>
        <Col className="gx-mt-4" xm={24} md={12}>
          <FormItem initialValue={initValue.cohort.id} name="cohort" label="Cohort" rules={[{ required: true, message: 'Provide cohort' }]}>
            <Select disabled loading={cohortLoading} placeholder="Select cohort">
              {cohort.map(({ id, name }) => (
                <Option value={id} key={id}>{name}</Option>
              ))}
            </Select>
          </FormItem>
        </Col>
        <Col className="gx-mt-4" xm={24} md={12}>
          <FormItem initialValue={initValue.subjects.map(({ id }) => id)} name="subjects" label="Subjects" rules={[{ required: true, message: 'Provide activity subjects' }]}>
            <Select disabled mode="multiple">
              {subjects.map(({ id, name }) => (
                <Option key={id} value={id}>{name}</Option>
              ))}
            </Select>
          </FormItem>
        </Col>
        <Col span={24}>
          <FormItem initialValue={initValue.hasTextInput} valuePropName="checked" name="hasTextInput">
            <Checkbox>Show text input?</Checkbox>
          </FormItem>
        </Col>
        <Col span={24}>
          <FormItem
            label="Attachment"
            valuePropName="file"
            name="attachment"
          >
            <Upload
              listType="picture"
              onRemove={(file) => setFileDeleteCache([...fileDeleteCache, { id: file.uid }])}
              beforeUpload={() => false}
              defaultFileList={makeDefaultFileList()}
            >
              <Button icon={<UploadOutlined />}>Attach files</Button>
            </Upload>
          </FormItem>
        </Col>
      </Row>
    </Form>
  );
};

EditActivity.propTypes = {
  cohort: arrayOf(shape({})).isRequired,
  setLoading: func.isRequired,
  cohortLoading: bool.isRequired,
  closeModal: func.isRequired,
  form: shape({}).isRequired,
  initValue: shape({}).isRequired,
};

export default EditActivity;
